import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getSocietyAdminData,
  getCommitteeMembers,
  getNonCommitteeFlatOwners,
  getAllRoles,
  assignRoleToUser,
  updateUserRoleInSociety,
  removeUserRoleFromSociety,
  CommitteeMember,
  NonCommitteeResident,
  SocietyRole,
} from '../services/api';
import { HiBadgeCheck, HiRefresh, HiSearch, HiPhone } from '../components/Icons';
import './SocietyAdminCommittee.css';

const COMMITTEE_ROLE_NAMES = new Set(['president', 'treasurer', 'body_member']);

function formatRoleName(role: string): string {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function flatLabel(flatNumber?: string, blockName?: string | null): string {
  if (!flatNumber) return '—';
  return blockName ? `${flatNumber}, ${blockName}` : flatNumber;
}

function roleBadgeClass(roleName: string): string {
  const key = roleName.toLowerCase().replace(/\s+/g, '_');
  if (key === 'president' || key === 'treasurer' || key === 'body_member' || key === 'normal_member') {
    return `sac-role-${key}`;
  }
  return 'sac-role-default';
}

type AssignTarget = {
  userId: number;
  name: string;
  flatLabel: string;
  isCommittee: boolean;
  currentRole?: string;
};

const SocietyAdminCommittee: React.FC = () => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [committee, setCommittee] = useState<CommitteeMember[]>([]);
  const [residents, setResidents] = useState<NonCommitteeResident[]>([]);
  const [roles, setRoles] = useState<SocietyRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const [assignModal, setAssignModal] = useState<AssignTarget | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('');

  const assignableRoles = useMemo(
    () => roles.filter((r) => r.name.toLowerCase() !== 'guard'),
    [roles]
  );

  const fetchData = useCallback(async () => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [committeeRes, residentsRes, rolesRes] = await Promise.all([
        getCommitteeMembers(societyId),
        getNonCommitteeFlatOwners(societyId),
        getAllRoles(),
      ]);
      if (committeeRes.success) setCommittee(committeeRes.data || []);
      if (residentsRes.success) setResidents(residentsRes.data || []);
      if (rolesRes.success) setRoles(rolesRes.data || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load committee data');
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!alert) return;
    const t = setTimeout(() => setAlert(null), 4000);
    return () => clearTimeout(t);
  }, [alert]);

  const q = searchQuery.toLowerCase().trim();
  const filteredCommittee = committee.filter((m) => {
    if (!q) return true;
    return (
      m.user_name.toLowerCase().includes(q) ||
      (m.user_phone || '').includes(q) ||
      (m.role_name || '').toLowerCase().includes(q) ||
      flatLabel(m.flat_number, m.block_name).toLowerCase().includes(q)
    );
  });

  const filteredResidents = residents.filter((m) => {
    if (!q) return true;
    return (
      m.user_name.toLowerCase().includes(q) ||
      (m.user_phone || '').includes(q) ||
      flatLabel(m.flat_number, m.block_name).toLowerCase().includes(q)
    );
  });

  const openAssignModal = (target: AssignTarget, preselectRoleId?: number) => {
    setAssignModal(target);
    setSelectedRoleId(preselectRoleId ?? '');
  };

  const closeAssignModal = () => {
    setAssignModal(null);
    setSelectedRoleId('');
  };

  const handleSaveRole = async () => {
    if (!assignModal || !selectedRoleId || !societyId) return;
    setSaving(true);
    try {
      let res;
      if (assignModal.isCommittee) {
        res = await updateUserRoleInSociety(assignModal.userId, societyId, Number(selectedRoleId));
      } else {
        res = await assignRoleToUser({
          userId: assignModal.userId,
          roleId: Number(selectedRoleId),
          societyId,
        });
        if (!res.success) {
          res = await updateUserRoleInSociety(assignModal.userId, societyId, Number(selectedRoleId));
        }
      }
      if (res.success) {
        setAlert({ type: 'success', message: res.message || 'Role updated successfully' });
        closeAssignModal();
        fetchData();
      } else {
        setAlert({ type: 'error', message: 'Failed to update role' });
      }
    } catch (err: unknown) {
      setAlert({ type: 'error', message: err instanceof Error ? err.message : 'Failed to update role' });
    } finally {
      setSaving(false);
    }
  };

  const handleDemoteToNormal = async (member: CommitteeMember) => {
    if (!societyId) return;
    const normalRole = roles.find((r) => r.name.toLowerCase() === 'normal_member');
    if (!normalRole) {
      setAlert({ type: 'error', message: 'Normal member role not found in system' });
      return;
    }
    if (
      !window.confirm(
        `Remove ${member.user_name} from committee and set as Normal Member?`
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await removeUserRoleFromSociety(member.user_id, societyId);
      const res = await assignRoleToUser({
        userId: member.user_id,
        roleId: normalRole.id,
        societyId,
      });
      if (res.success) {
        setAlert({ type: 'success', message: 'Member moved to normal member role' });
        fetchData();
      } else {
        setAlert({ type: 'error', message: 'Role removed but failed to assign normal member' });
        fetchData();
      }
    } catch (err: unknown) {
      setAlert({ type: 'error', message: err instanceof Error ? err.message : 'Failed to remove role' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="sac-loading">
        <div className="sac-spinner" />
        <p>Loading committee…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sac-error">
        <p>{error}</p>
        <button type="button" className="sac-btn sac-btn-primary" onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="sac-container">
      {alert && (
        <div className={`sac-alert sac-alert-${alert.type}`}>
          <span>{alert.message}</span>
          <button type="button" className="sac-alert-close" onClick={() => setAlert(null)}>
            &times;
          </button>
        </div>
      )}

      <header className="sac-header">
        <div className="sac-header-left">
          <HiBadgeCheck className="sac-header-icon" />
          <h1>Committee & Roles</h1>
        </div>
        <button type="button" className="sac-btn sac-btn-outline" onClick={fetchData} disabled={saving}>
          <HiRefresh /> Refresh
        </button>
      </header>

      <p className="sac-intro">
        Assign society roles such as President, Treasurer, and Body Member. Residents with other roles
        appear below — use <strong>Assign role</strong> to promote them to committee positions.
      </p>

      <div className="sac-search-bar">
        <HiSearch className="sac-search-icon" />
        <input
          type="text"
          placeholder="Search by name, phone, flat, or role…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <section className="sac-section">
        <h2 className="sac-section-title">Committee members ({filteredCommittee.length})</h2>
        {filteredCommittee.length === 0 ? (
          <p className="sac-empty">No committee members yet. Assign roles from the list below.</p>
        ) : (
          <div className="sac-table-wrap">
            <table className="sac-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Flat</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCommittee.map((m) => (
                  <tr key={m.id}>
                    <td className="sac-cell-name">{m.user_name}</td>
                    <td>
                      <span className={`sac-role-badge ${roleBadgeClass(m.role_name)}`}>
                        {formatRoleName(m.role_name)}
                      </span>
                    </td>
                    <td>{flatLabel(m.flat_number, m.block_name)}</td>
                    <td>
                      {m.user_phone ? (
                        <span>
                          <HiPhone style={{ width: 14, height: 14, verticalAlign: 'middle' }} />{' '}
                          {m.user_phone}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <div className="sac-actions">
                        <button
                          type="button"
                          className="sac-btn sac-btn-primary sac-btn-sm"
                          disabled={saving}
                          onClick={() =>
                            openAssignModal(
                              {
                                userId: m.user_id,
                                name: m.user_name,
                                flatLabel: flatLabel(m.flat_number, m.block_name),
                                isCommittee: true,
                                currentRole: m.role_name,
                              },
                              m.role_id
                            )
                          }
                        >
                          Change role
                        </button>
                        {COMMITTEE_ROLE_NAMES.has(m.role_name.toLowerCase()) && (
                          <button
                            type="button"
                            className="sac-btn sac-btn-danger-outline sac-btn-sm"
                            disabled={saving}
                            onClick={() => handleDemoteToNormal(m)}
                          >
                            Remove from committee
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="sac-section">
        <h2 className="sac-section-title">Other residents ({filteredResidents.length})</h2>
        {filteredResidents.length === 0 ? (
          <p className="sac-empty">No other flat members to assign.</p>
        ) : (
          <div className="sac-table-wrap">
            <table className="sac-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Flat</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredResidents.map((m) => (
                  <tr key={m.user_id}>
                    <td className="sac-cell-name">{m.user_name}</td>
                    <td>{flatLabel(m.flat_number, m.block_name)}</td>
                    <td>{m.user_phone || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="sac-btn sac-btn-primary sac-btn-sm"
                        disabled={saving}
                        onClick={() =>
                          openAssignModal({
                            userId: m.user_id,
                            name: m.user_name,
                            flatLabel: flatLabel(m.flat_number, m.block_name),
                            isCommittee: false,
                          })
                        }
                      >
                        Assign role
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {assignModal && (
        <div className="sac-modal-overlay" onClick={closeAssignModal}>
          <div
            className="sac-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="sac-assign-title"
          >
            <h3 id="sac-assign-title">
              {assignModal.isCommittee ? 'Change role' : 'Assign role'}
            </h3>
            <p className="sac-modal-sub">
              <strong>{assignModal.name}</strong>
              {assignModal.flatLabel !== '—' ? ` · ${assignModal.flatLabel}` : ''}
              {assignModal.currentRole ? (
                <>
                  <br />
                  Current: {formatRoleName(assignModal.currentRole)}
                </>
              ) : null}
            </p>
            <div className="sac-form-field">
              <label htmlFor="sac-role-select">Society role</label>
              <select
                id="sac-role-select"
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Select a role</option>
                {assignableRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {formatRoleName(r.name)}
                  </option>
                ))}
              </select>
            </div>
            <div className="sac-modal-actions">
              <button type="button" className="sac-btn sac-btn-outline" onClick={closeAssignModal}>
                Cancel
              </button>
              <button
                type="button"
                className="sac-btn sac-btn-primary"
                disabled={saving || !selectedRoleId}
                onClick={handleSaveRole}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminCommittee;
